import {
  BaseNote,
  getMidiNumberFromNoteTuple,
  noteFromOsmdPitch,
} from "Phaser/Util/OSMDHelper";
import { ClefType, Config, CursorData, SizingProps } from "./Types";
import { getNoteLineNumber } from "./Clef";

export type ExtraStaffParams = {
  topClefNumExtraLinesTop: number;
  topClefNumExtraLinesBottom: number;
  bottomClefNumExtraLinesTop: number;
  bottomClefNumExtraLinesBottom: number;
};

export type ClefHeight = {
  /* For the treble clef: the 'inner height' is measured from the highest pitch line
  to the lowest pitch line including ledger lines.
  For the bass clef: the 'inner height' is measured from the lowest pitch line
  to the highest pitch line including ledger lines.*/
  innerHeight: number;
  /* The outer height is measured from the highest pitch line (including ledger lines)
   * to the lowest pitch line (including ledger lines) */
  outerHeight: number;
};

function calculateNumberOfLinesInClef(
  cursorData: CursorData["serializedIterator"],
  clef: ClefType,
  //numExtraLinesTop: number,
  //numExtraLinesBottom: number,
): number {
  let minNote = [Infinity, ["C", Infinity]];
  let maxNote = [-Infinity, ["C", -Infinity]];
  cursorData.forEach(({ currentVoiceEntries }) =>
    currentVoiceEntries.forEach(({ Notes }) =>
      Notes.forEach(({ pitch, isRest }) => {
        if (isRest) return;
        // get midi number from pitch
        const note = noteFromOsmdPitch(pitch);
        const midiNumber = getMidiNumberFromNoteTuple(note);
        if (midiNumber < (minNote[0] as number)) {
          minNote = [midiNumber, note];
        }
        if (midiNumber > (maxNote[0] as number)) {
          maxNote = [midiNumber, note];
        }
      }),
    ),
  );
  const clefDefaultLowestLineNumber = 8;
  const clefDefaultHighestLineNumber = 0;
  const _minNoteLineNumber = getNoteLineNumber(
    0,
    clef,
    minNote[1] as [BaseNote, number],
  );
  const minNoteLineNumber = 14;
  const _maxNoteLineNumber = getNoteLineNumber(
    0,
    clef,
    maxNote[1] as [BaseNote, number],
  );
  const maxNoteLineNumber = -4;
  const outerHeightLineDiff =
    Math.max(minNoteLineNumber, clefDefaultLowestLineNumber) -
    Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug(
    "[PhraseOptimizer::calculateNumberOfLinesInClef] outerHeightLineDiff",
    outerHeightLineDiff,
    clef,
  );
  return outerHeightLineDiff;
}

export function computeClefHeightWithExtraLines(
  clef: ClefType,
  config: Config,
  numExtraLinesTop: number,
  numExtraLinesBottom: number,
): ClefHeight {
  const defaultNumberOfLines = 8;
  const numberOfLines =
    defaultNumberOfLines + numExtraLinesTop + numExtraLinesBottom;
  const outerHeightLineDiff = numberOfLines;
  const innerHeightLineDiff =
    clef === "treble"
      ? numberOfLines - numExtraLinesTop
      : numberOfLines - numExtraLinesBottom;
  const outerHeight = (outerHeightLineDiff / 2) * config.pitchLineGap;
  const innerHeight = (innerHeightLineDiff / 2) * config.pitchLineGap;
  return {
    innerHeight,
    outerHeight,
  };
}

export function computeClefHeight(
  cursorData: CursorData["serializedIterator"],
  clef: ClefType,
  config: Config,
): ClefHeight {
  const { pitchLineGap } = config;
  let minNote: [number, [BaseNote, number]] | undefined;
  let maxNote: [number, [BaseNote, number]] | undefined;
  cursorData.forEach(({ currentVoiceEntries }) =>
    currentVoiceEntries.forEach(({ Notes }) =>
      Notes.forEach(({ pitch, isRest, staffPosition }) => {
        if (
          isRest ||
          (clef === "treble"
            ? staffPosition != "top"
            : staffPosition != "bottom")
        )
          return;
        // get midi number from pitch
        const note = noteFromOsmdPitch(pitch);
        const midiNumber = getMidiNumberFromNoteTuple(note);
        if (minNote === undefined || midiNumber < (minNote[0] as number)) {
          minNote = [midiNumber, note];
        }
        if (maxNote === undefined || midiNumber > (maxNote[0] as number)) {
          maxNote = [midiNumber, note];
        }
      }),
    ),
  );
  const clefDefaultLowestLineNumber = 8;
  const clefDefaultHighestLineNumber = 0;
  const _minNoteLineNumber =
    minNote != undefined
      ? getNoteLineNumber(0, clef, minNote[1] as [BaseNote, number])
      : clefDefaultLowestLineNumber;
  const _maxNoteLineNumber =
    maxNote != undefined
      ? getNoteLineNumber(0, clef, maxNote[1] as [BaseNote, number])
      : clefDefaultHighestLineNumber;
  const minNoteLineNumber = 14;
  const maxNoteLineNumber = -4;
  const outerHeightLineDiff =
    Math.max(minNoteLineNumber, clefDefaultLowestLineNumber) -
    Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug(
    "[PhraseOptimizer] minNoteLNo",
    minNoteLineNumber,
    "maxNoteLno",
    maxNoteLineNumber,
    minNote,
    maxNote,
    clef,
  );
  const innerHeightLineDiff =
    clef === "treble"
      ? Math.max(clefDefaultLowestLineNumber, minNoteLineNumber) -
        clefDefaultHighestLineNumber
      : clefDefaultLowestLineNumber -
        Math.min(maxNoteLineNumber, clefDefaultHighestLineNumber);
  console.debug(
    "[PhraseOptimizer::computeClefHeight] clefDefaultLowestLineNumber=",
    clefDefaultLowestLineNumber,
    "clefDefaultHighestLineNumber=",
    clefDefaultHighestLineNumber,
    "outerHeightLineDiff=",
    outerHeightLineDiff,
    "innerHeightLineDiff=",
    innerHeightLineDiff,
  );
  const outerHeight = (outerHeightLineDiff / 2) * pitchLineGap;
  const innerHeight = (innerHeightLineDiff / 2) * pitchLineGap;
  /*let lowestLineNumber = 0;
  let highestLineNumber = 0;
  if (clef === "treble") {
    lowestLineNumber = getNoteLineNumber(0, clef, ["E", 4]);
    highestLineNumber = getNoteLineNumber(0, clef, ["F", 5]);
  } else {
    lowestLineNumber = getNoteLineNumber(0, clef, ["G", 2]);
    highestLineNumber = getNoteLineNumber(0, clef, ["A", 3]);
  }
  const minLineNumber = Math.min(
    lowestLineNumber,
    getNoteLineNumber(0, clef, minNote[1] as [BaseNote, number]),
  );
  const maxLineNumber = Math.max(
    highestLineNumber,
    getNoteLineNumber(0, clef, maxNote[1] as [BaseNote, number]),
  );
  // NOTE: This doesn't currently account for finger numbers or other symbols that may be added to the staff line.
  const outerHeight =
    (minLineNumber - maxLineNumber + 1) * pitchLineGap + pitchLineGap * 0.5;
  const innerHeight =
    clef === "treble"
      ? (minLineNumber - highestLineNumber + 1) * pitchLineGap
      : (lowestLineNumber - maxLineNumber + 1) * pitchLineGap;
      */
  /*console.debug(
    "[PhraseOptimizer::computeClefHeight] Computed",
    clef,
    "clef height: minNote=",
    minNote,
    "maxNote=",
    maxNote,
    "minNoteLineNumber=",
    minNoteLineNumber,
    "maxNoteLineNumber=",
    maxNoteLineNumber,
    "innerHeight=",
    innerHeight,
    "outerHeight=",
    outerHeight,
  );*/
  return {
    innerHeight,
    outerHeight,
  };
}

export function computeStaffLineHeight(
  //cursorData: CursorData["serializedIterator"],
  config: Config,
  extraStaffParams: ExtraStaffParams,
): PhraseDimens {
  const {
    topClefNumExtraLinesTop,
    topClefNumExtraLinesBottom,
    bottomClefNumExtraLinesTop,
    bottomClefNumExtraLinesBottom,
  } = extraStaffParams;
  const topClefHeight = computeClefHeightWithExtraLines(
    "treble",
    config,
    topClefNumExtraLinesTop,
    topClefNumExtraLinesBottom,
  );
  const bottomClefHeight = computeClefHeightWithExtraLines(
    "bass",
    config,
    bottomClefNumExtraLinesTop,
    bottomClefNumExtraLinesBottom,
  );
  const { clefGap } = config;
  /*const clefGapWithExtraLines =
    clefGap +
    ((topClefNumExtraLinesBottom + bottomClefNumExtraLinesTop) / 2 + 1) *
      config.pitchLineGap;*/
  const clefGapWithExtraLines = clefGap + config.pitchLineGap;
  const innerHeight =
    topClefHeight.innerHeight +
    bottomClefHeight.innerHeight +
    clefGapWithExtraLines;
  return {
    bbHeight:
      topClefHeight.outerHeight +
      bottomClefHeight.outerHeight +
      clefGapWithExtraLines,
    topClefHeight,
    bottomClefHeight,
    topClefNumExtraLinesTop,
    topClefNumExtraLinesBottom,
    bottomClefNumExtraLinesTop,
    bottomClefNumExtraLinesBottom,
    innerHeight,
  };
}

const checkStaffLinesFit = (
  phraseDimens: PhraseDimens,
  availableHeight: number,
  availableWidth: number,
  minGapBetweenStaffLines: number,
  minAspectRatio: number,
  config: Config,
  numStaffLines: number = 2,
): boolean => {
  const minVpHeightAvailable = availableHeight - minGapBetweenStaffLines * (numStaffLines - 1);
  const fitsVertically = phraseDimens.bbHeight * numStaffLines <= minVpHeightAvailable;
  //const minPhraseWidthRequired = minAspectRatio * phraseDimens.innerHeight;
  //const fitsHorizontally = minPhraseWidthRequired <= availableWidth;
  //const fitsHorizontally = true;
  console.debug(
    "[PhraseOptimizer] Checking fit: availableHeight=",
    availableHeight,
    "availableWidth=",
    availableWidth,
    "phraseHeight=",
    phraseDimens.bbHeight,
    "fitsVertically=",
    fitsVertically,
    //"fitsHorizontally=",
    //fitsHorizontally,
  );
  return /*fitsHorizontally &&*/ fitsVertically;
};

export interface PhraseDimens {
  bbHeight: number;
  innerHeight: number;
  topClefHeight: ClefHeight;
  bottomClefHeight: ClefHeight;
  topClefNumExtraLinesTop: number;
  topClefNumExtraLinesBottom: number;
  bottomClefNumExtraLinesTop: number;
  bottomClefNumExtraLinesBottom: number;
}

export type ResizeParams = {
  paddingY: number;
  paddingX: number;
  staffSpacingVertical: number;
  phraseRenderingConfig: Config;
  phraseDimens: PhraseDimens;
  fit?: boolean;
};

export function optimizeStaffForOptions(
  vpDimens: [number, number],
  props: SizingProps,
  extraStaffParams: ExtraStaffParams,
  minAspectRatio: number,
  maxAspectRatio: number,
  numStaffLines: number = 2,
): ResizeParams | null {
  const [vpWidth, vpHeight] = vpDimens;
  const {
    minPaddingX,
    minPaddingY,
    idealPaddingX,
    idealPaddingY,
    idealPhraseConfig,
    minPhraseConfig,
    minStaffSpacingVertical,
  } = props;
  let { clefGap } = idealPhraseConfig;
  const maxClefGap = idealPhraseConfig.pitchLineGap * 2 + clefGap;
  const maxStaffSpacing = maxClefGap * 2 - clefGap;
  let staffSpacingVertical =
    minStaffSpacingVertical + idealPhraseConfig.pitchLineGap;
  const phraseDimens = computeStaffLineHeight(
    idealPhraseConfig,
    extraStaffParams,
  );
  const availableHeightIdeal = vpHeight - idealPaddingY * 2;
  const availableWidthIdeal = vpWidth - idealPaddingX * 2;
  const availableHeightMin = vpHeight - minPaddingY * 2;
  const availableWidthMin = vpWidth - minPaddingX * 2;
  let paddingY = idealPaddingY;
  let paddingX = idealPaddingX;
  if (
    checkStaffLinesFit(
      phraseDimens,
      availableHeightIdeal,
      availableWidthIdeal,
      staffSpacingVertical,
      minAspectRatio,
      idealPhraseConfig,
      numStaffLines,
    )
  ) {
    console.debug("[PhraseOptimizer] fits ideal config and padding");
    let heightToSpare =
      vpHeight -
      phraseDimens.bbHeight * numStaffLines -
      idealPaddingY * 2 -
      staffSpacingVertical * (numStaffLines - 1);
    const extraStaffSpacing = Math.max(
      0,
      Math.min(maxStaffSpacing - staffSpacingVertical, (heightToSpare / 2) / (numStaffLines - 1)),
    );
    heightToSpare -= extraStaffSpacing * (numStaffLines - 1);
    const extraClefGap = Math.max(
      0,
      Math.min(maxClefGap - clefGap, heightToSpare / 4),
    );
    heightToSpare -= extraClefGap * 2;
    paddingY += heightToSpare / 2;
    clefGap += extraClefGap;
    staffSpacingVertical += extraStaffSpacing;
    const maxPhraseWidth = phraseDimens.innerHeight * maxAspectRatio;
    console.debug("[PhraseOptimizer]", {
      vpWidth,
      maxPhraseWidth,
      availableWidthIdeal,
      innerHeight: phraseDimens.innerHeight,
    });
    if (maxPhraseWidth < availableWidthIdeal) {
      paddingX += (availableWidthIdeal - maxPhraseWidth) / 2;
    }
    const phraseRenderingConfig = {
      ...idealPhraseConfig,
      clefGap,
    };
    return {
      phraseDimens: computeStaffLineHeight(
        phraseRenderingConfig,
        extraStaffParams,
      ),
      paddingX,
      paddingY,
      staffSpacingVertical,
      phraseRenderingConfig,
      fit: true,
    };
  } else if (
    checkStaffLinesFit(
      phraseDimens,
      availableHeightMin,
      availableWidthMin,
      minStaffSpacingVertical,
      minAspectRatio,
      idealPhraseConfig,
      numStaffLines,
    )
  ) {
    console.debug(
      "[PhraseOptimizer] fits ideal config with min padding",
      phraseDimens,
      vpHeight,
    );
    let heightToSpare =
      vpHeight -
      phraseDimens.bbHeight * 2 -
      minPaddingY * 2 -
      minStaffSpacingVertical * (numStaffLines - 1);
    const extraStaffSpacingVertical = Math.min(
      Math.max(0, heightToSpare / (numStaffLines - 1)),
      staffSpacingVertical - minStaffSpacingVertical,
    );
    heightToSpare -= extraStaffSpacingVertical * (numStaffLines - 1);
    paddingY = Math.max(minPaddingY, minPaddingY + heightToSpare / 2);
    staffSpacingVertical = minStaffSpacingVertical + extraStaffSpacingVertical;
    const maxPhraseWidth = phraseDimens.innerHeight * maxAspectRatio;
    if (maxPhraseWidth < availableWidthMin) {
      paddingX += (availableWidthMin - maxPhraseWidth) / 2;
    }
    return {
      phraseDimens,
      paddingX,
      paddingY,
      staffSpacingVertical,
      phraseRenderingConfig: idealPhraseConfig,
      fit: true,
    };
  } else {
    const phraseDimens = computeStaffLineHeight(
      minPhraseConfig,
      extraStaffParams,
    );
    if (
      checkStaffLinesFit(
        phraseDimens,
        availableHeightMin,
        availableWidthMin,
        minStaffSpacingVertical,
        minAspectRatio,
        minPhraseConfig,
        numStaffLines,
      )
    ) {
      console.debug("[PhraseOptimizer] fits min config with min padding");
      const availableVerticalSpace =
        vpHeight -
        minPaddingY * 2 -
        minStaffSpacingVertical -
        minPhraseConfig.clefGap * 2;
      const numberOfLinesInClef =
        extraStaffParams.topClefNumExtraLinesTop +
        extraStaffParams.topClefNumExtraLinesBottom +
        8;
      const numberOfLinesForClefGap = 2;
      const numberOfLinesInStaff =
        numberOfLinesInClef * numStaffLines + numberOfLinesForClefGap;
      /*const totalNumberOfLines = (calculateNumberOfLinesInClef(cursorData1, "treble") - 1.5)
      + (calculateNumberOfLinesInClef(cursorData1, "bass") - 1.5)
      + (calculateNumberOfLinesInClef(cursorData2, "treble") - 1.5)
      + (calculateNumberOfLinesInClef(cursorData2, "bass") - 1.5);*/
      const totalNumberOfLines = numberOfLinesInStaff * 2;
      const pitchLineGap = availableVerticalSpace / (totalNumberOfLines / 2);
      console.debug(
        "[PhraseOptimizer] computing space for ",
        totalNumberOfLines,
        "lines, availableVerticalSpace",
        availableVerticalSpace,
        "pitchLineGap",
        pitchLineGap,
        "minPadY",
        minPaddingY,
        "minPadX",
        minPaddingX,
      );
      staffSpacingVertical = minStaffSpacingVertical;
      const phraseRenderingConfig = {
        ...minPhraseConfig,
        pitchLineGap,
      };
      const phraseDimens = computeStaffLineHeight(
        phraseRenderingConfig,
        extraStaffParams,
      );
      paddingY = minPaddingY;
      paddingX = minPaddingX;
      const maxPhraseWidth = phraseDimens.innerHeight * maxAspectRatio;
      if (maxPhraseWidth < availableWidthMin) {
        paddingX += (availableWidthMin - maxPhraseWidth) / 2;
      }
      return {
        phraseDimens,
        paddingX,
        paddingY,
        staffSpacingVertical,
        phraseRenderingConfig,
        fit: true,
      };
    } else {
      //console.warn('[PhraseOptimizer] not enough space for staff lines!', vpDimens, props, extraStaffParams);
      return null;
    }
  }
}

export function optimizeStaffLineHeight(
  vpDimens: [number, number],
  sizingProps: {
    min: SizingProps;
    max: SizingProps;
  },
  minAspectRatio: number,
  maxAspectRatio: number,
): ResizeParams {
  const { min, max } = sizingProps;
  const extraStaffParams = {
    topClefNumExtraLinesTop: 4,
    topClefNumExtraLinesBottom: 2,
    bottomClefNumExtraLinesTop: 2,
    bottomClefNumExtraLinesBottom: 4,
  };
  return (
    optimizeStaffForOptions(
      vpDimens,
      max,
      extraStaffParams,
      minAspectRatio,
      maxAspectRatio,
    ) ??
    optimizeStaffForOptions(
      vpDimens,
      min,
      extraStaffParams,
      minAspectRatio,
      maxAspectRatio,
    ) ?? {
      phraseDimens: computeStaffLineHeight(
        min.minPhraseConfig,
        extraStaffParams,
      ),
      paddingX: min.minPaddingX,
      paddingY: min.minPaddingY,
      staffSpacingVertical: min.minStaffSpacingVertical,
      phraseRenderingConfig: min.minPhraseConfig,
    }
  );
}

export function getBraceWidth(phraseDimens: PhraseDimens, scene: Phaser.Scene) {
  const brace = scene.add.image(-10000, -10000, "brace");
  brace.setAlpha(0);
  const aspectRatio = brace.width / brace.height;
  return (phraseDimens.innerHeight * aspectRatio);
}